import { initSentry } from './lib/sentry'
initSentry()

import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import { isPwaHost } from './lib/pwaHost'
import './lib/i18n'
// Geist Variable loaded from JS so Vite copies the .woff2 files to
// dist/assets/. Importing it via CSS @import (Tailwind v4) inlines the
// stylesheet but loses the node_modules context for url(./files/…),
// producing 404s on the woff2 requests in production preview/Netlify
// (browser falls back to system-ui → "Closez plus / Closez mieux"
// collapses from 2 lines to 1, €67/mois reflows, slash navbar drifts).
// The JS-side import is the canonical fontsource pattern.
import '@fontsource-variable/geist/index.css'
import './index.css'
import App from './App.tsx'

// PWA gating (2026-04-18):
//   - Production domain (betterclose.io) → register the Workbox SW,
//     run the install prompt, serve offline.html fallback.
//   - Preview deploys (dev--/staging--.netlify.app) + localhost →
//     DO NOT register the SW. Also, actively unregister any SW that a
//     previous visit left behind and clear its caches, so users stop
//     seeing stale bundles or the "Hors ligne" fallback between deploys.
//
// Why NOT rely on vite-plugin-pwa injectRegister:
// - UpdateAvailableModal.tsx already owns the "a new version is ready"
//   UX via `version.json` polling. The SW's waiting -> active handoff
//   must NOT race that modal or the user would see two prompts.
// - onNeedRefresh stays a no-op — we let the Modal drive the reload
//   choreography. onOfflineReady just logs so we can verify install.
if (import.meta.env.PROD && isPwaHost()) {
  registerSW({
    immediate: true,
    onNeedRefresh() {
      // Intentional no-op: UpdateAvailableModal handles the refresh prompt.
    },
    onOfflineReady() {
      if (import.meta.env.DEV) console.info('[bc] PWA: offline-ready')
    },
    onRegisteredSW(swUrl) {
      if (import.meta.env.DEV) console.info('[bc] PWA: SW registered at', swUrl)
    },
  })
} else if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  // Cleanup any SW left over from an earlier visit (e.g., when
  // dev--betterclose.netlify.app used to register one). Non-blocking.
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .catch(() => {
      /* offline or permission quirk — harmless, nothing to do */
    })
  if ('caches' in window) {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => {
        /* same — best-effort cleanup */
      })
  }
}

/**
 * Vite chunk-load failure recovery.
 *
 * When Vite re-optimizes deps (dev) or ships a new deploy with re-hashed
 * chunks (prod), any in-flight lazy imports that reference the OLD chunk
 * URLs fail with "Failed to fetch dynamically imported module". The user
 * sees the ErrorBoundary screen ("Quelque chose s'est mal passé") and has
 * to hard-refresh. Known, documented Vite behavior — the official fix
 * (https://vitejs.dev/guide/build.html#load-error-handling) is to listen
 * for `vite:preloadError` on the window and hard-reload when it fires.
 *
 * We also guard against a reload loop: only reload once per tab session
 * using sessionStorage, so if the reload itself re-triggers the error
 * (unlikely but possible), we don't loop forever.
 */
const CHUNK_RELOAD_KEY = '__bc_chunk_reload_fired__'

function reloadForChunkFailure(reason: string) {
  try {
    const already = sessionStorage.getItem(CHUNK_RELOAD_KEY)
    if (already === '1') {
      console.warn('[bc] chunk reload already fired this session, not looping', reason)
      return
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  } catch {
    // sessionStorage unavailable — still safer to reload once than to leave the user stuck
  }
  console.warn('[bc] Vite chunk load failed, hard-reloading:', reason)
  window.location.reload()
}

// Clear the one-shot guard on a successful load
window.addEventListener('load', () => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    /* noop */
  }
})

// Vite's official event — dev and prod
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  reloadForChunkFailure('vite:preloadError')
})

// Belt-and-braces: catch dynamic import errors that slip past Vite's listener
// (e.g., when React.lazy swallows the rejection before vite:preloadError fires).
window.addEventListener('unhandledrejection', (event) => {
  const msg = String(event.reason?.message ?? event.reason ?? '')
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes("Importing a module script failed")
  ) {
    event.preventDefault()
    reloadForChunkFailure('unhandledrejection: ' + msg.slice(0, 120))
  }
})

createRoot(document.getElementById('root')!, {
  // React 19 error hooks — capture all errors in Sentry
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    if (import.meta.env.DEV) console.error('Uncaught error:', error, errorInfo.componentStack)
  }),
  onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    if (import.meta.env.DEV) console.error('Caught error:', error, errorInfo.componentStack)
  }),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
